import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, User } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const storedLang = localStorage.getItem('janus_lang');
  const [lang, setLang] = useState((storedLang === 'Fr' ? 'Fr' : 'En'));
  const location = useLocation();
  const navigate = useNavigate();

  const isDarkPage = location.pathname === '/';
  const textColor = (scrolled || isOpen || !isDarkPage) ? 'text-[#C5A059]' : 'text-white';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setIsOpen(false), [location]);

  useEffect(() => {
    const handleLangChange = () => {
        const stored = localStorage.getItem('janus_lang');
        setLang((stored === 'Fr' ? 'Fr' : 'En'));
      };
    window.addEventListener('langchange', handleLangChange);
    return () => window.removeEventListener('langchange', handleLangChange);
  }, []);

  const changeLang = (newLang) => {
    setLang(newLang);
    localStorage.setItem('janus_lang', newLang === 'Fr' ? 'Fr' : 'En');
    window.dispatchEvent(new Event('langchange'));
  };

  const openContact = (e) => {
    if (e) e.preventDefault();
    if (typeof setIsOpen === 'function') setIsOpen(false);
    window.dispatchEvent(new Event('openContactModal'));
  };

  const navTranslations = {
    En: { bookSession: 'Book a session', private: 'Client Access', home: 'Janus Legacy', realEstate: 'International Real Estate', assetMgt: 'Asset Allocation', digital: 'Digital Asset', sov: 'Sovereignty', insights: 'Insights', contactUs: 'Contact Us' },
    Fr: { bookSession: 'Réserver une session', private: 'Accès Client', home: 'Janus Héritage', realEstate: 'Immobilier International', assetMgt: "Allocation d'Actifs", digital: 'Actifs Numériques', sov: 'Souveraineté', insights: 'Analyses', contactUs: 'Contactez-nous' }
  };
  const t = navTranslations[lang];

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolled || isOpen ? `bg-black/5 backdrop-blur-md ${textColor} shadow-lg py-4` : `bg-transparent ${textColor} py-6`}`} dir="ltr">
        <div className="w-full px-6 lg:px-12 flex justify-between items-center">
          
          {/* LOGO FINNALISÉ : Nouvelles arches exclusives de la marque */}
          <Link to="/" className="flex items-center gap-4 z-50 relative group">
            <img 
              src="https://januscapitalstrategy.com/uploads/images/1784356715733-logo_janus.webp" 
              alt="Janus Arche" 
              className="h-14 w-auto object-contain" 
            />
            <div className="flex flex-col justify-center mt-1">
              <span className={`text-2xl font-light tracking-widest uppercase transition-colors duration-500 ${textColor}`}>
                JANUS CAPITAL
              </span>
              <span className="text-[10px] font-medium tracking-[0.3em] uppercase mt-0.5 text-[#C5A059] group-hover:text-white transition-colors">
                YOUR EMPOWERED MONEY
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-6 z-50 relative">
            <button onClick={() => goBookSession(setIsOpen)} className="hidden md:block bg-[#0A1228] text-white border border-white/20 px-5 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-white hover:text-black transition">{t.bookSession || 'Book a session'}</button>
            <button onClick={openContact} className="hidden md:block bg-[#C5A059] text-white px-5 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-white hover:text-black transition">
              {t.contactUs}
            </button>

            <Link to="/portal" className="hidden md:flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest hover:text-[#C5A059] transition">
              <User size={14} /> {t.private}
            </Link>

            <div className="relative group cursor-pointer ml-2">
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest hover:text-[#C5A059] transition">
                <Globe size={14}/> {lang} <ChevronDown size={12}/>
              </div>
              <div className="absolute right-0 mt-2 w-24 bg-black text-white border border-white/10 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all flex flex-col overflow-hidden">
                <button onClick={() => changeLang('En')} className="px-4 py-3 text-left hover:bg-white/10 text-[10px] tracking-widest font-bold">ENGLISH</button>
                <button onClick={() => changeLang('Fr')} className="px-4 py-3 text-left hover:bg-white/10 text-[10px] tracking-widest font-bold">FRANÇAIS</button>              </div>
            </div>

            <button onClick={() => setIsOpen(!isOpen)} className="focus:outline-none hover:text-[#C5A059] transition-colors ml-4">
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* BANDEAU LATÉRAL */}
      <div className={`fixed inset-y-0 right-0 w-full md:w-[650px] bg-black/5 backdrop-blur-2xl border-l border-white/10 z-40 transition-transform duration-700 ease-in-out flex flex-col pt-28 pb-12 px-12 overflow-y-auto ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col gap-8 text-right flex-grow justify-start">
          
          <Link to="/janus" className={`flex items-center justify-end gap-4 text-3xl font-light ${textColor} hover:text-[#C5A059] transition-colors tracking-widest uppercase border-b border-white/10 pb-4`}>
            <img src="https://januscapitalstrategy.com/uploads/images/1784356715733-logo_janus.webp" alt="Janus Arche" className="h-12 w-auto object-contain" />
            {t.home}
          </Link>

          <Link to="/asset-management" className={`text-3xl font-light ${textColor} hover:text-[#C5A059] transition-colors tracking-widest uppercase`}>{t.assetMgt}</Link>
          <Link to="/portfolio" className={`text-3xl font-light ${textColor} hover:text-[#C5A059] transition-colors tracking-widest uppercase`}>{t.realEstate}</Link>
          <Link to="/digital-assets" className={`text-3xl font-light ${textColor} hover:text-[#C5A059] transition-colors tracking-widest uppercase`}>{t.digital}</Link>
          <Link to="/sovereignty" className={`text-3xl font-light ${textColor} hover:text-[#C5A059] transition-colors tracking-widest uppercase`}>{t.sov}</Link>
          <Link to="/insights" className={`text-3xl font-light ${textColor} hover:text-[#C5A059] transition-colors tracking-widest uppercase`}>{t.insights}</Link>
          
          <div className="mt-auto pt-8 border-t border-white/10 flex flex-col gap-4">
            <Link to="/portal" className="text-xl font-light text-[#C5A059] hover:text-white transition-colors tracking-widest uppercase text-right w-full flex items-center justify-end gap-2">
              <User size={18} /> {t.private}
            </Link>
            <button onClick={() => goBookSession(setIsOpen)} className={`text-2xl font-light ${textColor} hover:text-[#C5A059] transition-colors tracking-widest uppercase text-right w-full`}>{t.bookSession || 'Book a session'}</button>
            <button onClick={openContact} className="text-2xl font-light text-[#C5A059] hover:text-white transition-colors tracking-widest uppercase text-right w-full">{t.contactUs}</button>
          </div>
        </div>
      </div>
    </>
  );
}

function goBookSession(closeMenu) {
  if (window.location.pathname === '/') {
    document.getElementById('strategy-session')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    window.location.assign('/#strategy-session');
  }
  if (typeof closeMenu === 'function') closeMenu(false);
}
